/**
 * Centralized environment access.
 *
 * Vite only exposes variables prefixed with `VITE_` or `REACT_APP_` (see
 * `envPrefix` in vite.config.js) via `import.meta.env`, replacing CRA's
 * `process.env.REACT_APP_*` convention. This module is the single place
 * that reads `import.meta.env` so the rest of the app (and Jest, which
 * cannot parse `import.meta`) never references it directly.
 *
 * In Jest, `import.meta.env` does not exist, so this file is replaced by
 * `src/config/__mocks__/env.js` via the `env.js` -> jest moduleNameMapper
 * indirection is unnecessary — instead we simply guard access below and
 * jest.config.js maps this module to a CommonJS-safe implementation that
 * reads from `process.env` for tests.
 */

const viteEnv = import.meta.env || {};

export const NODE_ENV = viteEnv.MODE || "development";
export const isDevelopment = viteEnv.DEV ?? NODE_ENV === "development";
export const isProduction = viteEnv.PROD ?? NODE_ENV === "production";

// Production builds default to same-origin `/api` (nginx proxy). Localhost
// is only used in development so a missing build arg cannot silently point
// a production bundle at a developer machine.
export const API_URL =
  viteEnv.REACT_APP_API_URL ||
  viteEnv.VITE_API_URL ||
  (isProduction ? "/api" : "http://localhost:5000/api");
export const API_BASE =
  viteEnv.REACT_APP_API_BASE ||
  viteEnv.VITE_API_BASE ||
  (isProduction ? "" : "http://localhost:5000");
export const SOCKET_URL =
  viteEnv.REACT_APP_SOCKET_URL ||
  viteEnv.VITE_SOCKET_URL ||
  (isProduction ? "" : undefined);
export const SITE_URL = viteEnv.REACT_APP_SITE_URL || viteEnv.VITE_SITE_URL;
export const SENTRY_DSN =
  viteEnv.REACT_APP_SENTRY_DSN || viteEnv.VITE_SENTRY_DSN;

export const ADSENSE_CLIENT =
  viteEnv.REACT_APP_ADSENSE_CLIENT || viteEnv.VITE_ADSENSE_CLIENT || "";
export const ADSENSE_ENABLED_FLAG =
  viteEnv.REACT_APP_ADSENSE_ENABLED || viteEnv.VITE_ADSENSE_ENABLED;
export const ADSENSE_SLOT_PRODUCT =
  viteEnv.REACT_APP_ADSENSE_SLOT_PRODUCT ||
  viteEnv.VITE_ADSENSE_SLOT_PRODUCT ||
  "";
export const ADSENSE_SLOT_FEED =
  viteEnv.REACT_APP_ADSENSE_SLOT_FEED || viteEnv.VITE_ADSENSE_SLOT_FEED || "";
export const ADSENSE_SLOT_CATEGORY =
  viteEnv.REACT_APP_ADSENSE_SLOT_CATEGORY ||
  viteEnv.VITE_ADSENSE_SLOT_CATEGORY ||
  "";

// public: customer site only. admin: operator site only.
// anything else (including unset) keeps both, which is local development.
const rawSurface = String(
  viteEnv.REACT_APP_SURFACE || viteEnv.VITE_SURFACE || "all",
)
  .trim()
  .toLowerCase();
export const APP_SURFACE =
  rawSurface === "public" || rawSurface === "admin" ? rawSurface : "all";
export const includePublicRoutes = APP_SURFACE !== "admin";
export const includeAdminRoutes = APP_SURFACE !== "public";
export const isAdminSurface = APP_SURFACE === "admin";

const env = {
  NODE_ENV,
  isDevelopment,
  isProduction,
  API_URL,
  API_BASE,
  SOCKET_URL,
  SITE_URL,
  SENTRY_DSN,
  ADSENSE_CLIENT,
  ADSENSE_ENABLED_FLAG,
  ADSENSE_SLOT_PRODUCT,
  ADSENSE_SLOT_FEED,
  ADSENSE_SLOT_CATEGORY,
  APP_SURFACE,
  includePublicRoutes,
  includeAdminRoutes,
  isAdminSurface,
};

export default env;
